import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import {
  Layout,
  Modal,
  notification
} from '@lockerpm/design'

import {
  ExclamationCircleOutlined
} from '@ant-design/icons'

import commonComponents from './components/common'

import AdminLayout from './layouts/index'
import OtherLayout from './layouts/other'

import pages from './pages'

import authServices from './services/auth'
import userServices from './services/user'
import storeActions from './store/actions'

import common from './utils/common'
import global from './config/global'
import jsCore from './web-sh/src/core-js/index'
import { navigatePage } from './web-sh/src/utils/navigate'

import { service } from './web-sh/src/service'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

import './assets/css/index.scss'

const App = () => {
  window.service = service;
  const jsCoreGlobal = { lockerContainerService: null };
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const location = useLocation()
  
  const {
    ClientService,
    Onboarding,
    ChatwootWidget
  } = commonComponents

  global.location = location;
  notification.config({ placement: 'bottomLeft', duration: 3 })
  global.notification = (type, message, description) => { notification[type]({ message, description }) }
  global.navigate = (name, params = {}, query = {}, replace = false) => navigatePage(navigate, dispatch, name, params, query, replace)
  global.pushSuccess = message => {
    global.notification(
      'success',
      t('notification.success.title'),
      message
    )
  }
  global.pushError = error => {
    const { message, code } = common.getErrorMessage(error)
    if (code) {
      return global.notification(
        'error',
        t('notification.error.title'),
        t(`errors.${code}`)
      )
    }
    return global.notification(
      'error',
      t('notification.error.title'),
      message || t('notification.error.message.default')
    )
  }
  global.confirm = (handleOK = () => { }, options = {}) => Modal.confirm({
    title: options.title || t('common.confirm'),
    icon: <ExclamationCircleOutlined />,
    content: options.content || t('common.delete_question'),
    okText: options.okText || t('button.delete'),
    cancelText: t('button.cancel'),
    okButtonProps: options.okButtonProps || { danger: true },
    onOk: handleOK
  })

  const userInfo = useSelector(state => state.auth.userInfo)
  const currentPage = useSelector(state => state.system.currentPage)

  useEffect(() => {
    initJsCore()
    const currentPage = common.getRouterByLocation(location)
    const queryLang = currentPage?.query?.lang || common.getQueryLang();
    const isLang = Object.values(global.constants.LANGUAGE).includes(queryLang);
    if (isLang) {
      common.setQueryLang(queryLang);
      common.updateLocale(queryLang);
    } else {
      const locale = common.getLanguage()
      common.updateLocale(locale)
    }
    if (currentPage && !currentPage.not_auth) {
      authServices.redirect_login()
    }
  }, [])

  useEffect(() => {
    global.location = location;
    const currentPage = common.getRouterByLocation(location)
    dispatch(storeActions.updateCurrentPage(currentPage))
    beforeRedirect()
  }, [location])

  useEffect(() => {
    if (userInfo?.email) {
      const queryLang = common.getQueryLang();
      if (queryLang && userInfo?.customer_language !== queryLang) {
        userServices.update_me({ customer_language: queryLang }).then(() => {
          common.removeQueryLang();
          common.fetchUserInfo();
        }).catch((error) => {
          global.pushError(error)
        })
      }
    }
  }, [userInfo?.customer_language])

  const initJsCore = async () => {
    if (!global.jsCore) {
      global.jsCore = await jsCore(jsCoreGlobal)
    }
  }

  const beforeRedirect = async () => {
    const currentPage = common.getRouterByLocation(location)
    const accessToken = await common.getAccessToken()
    if (accessToken) {
      if (!currentPage) {
        authServices.redirect_login();
        return
      }
      if (!currentPage?.not_auth) {
        return
      }
    } else {
      if (!currentPage) {
        global.navigate(global.keys.ERROR_404)
        return
      }
    }
  }

  return (
    <Layout className='main-layout'>
      <ClientService />
      {
        currentPage?.type === 'admin' && userInfo?.email && <>
          <Onboarding />
          <AdminLayout
            routers={common.adminRouters()}
            pages={pages}
          />
        </>
      }
      {
        currentPage?.type === 'auth' && <OtherLayout
          routers={global.routers.AUTH_ROUTERS}
          pages={pages}
        />
      }
      {
        currentPage?.type === 'error' && <OtherLayout
          routers={global.routers.ERROR_ROUTERS}
          pages={pages}
        />
      }
      {
        currentPage?.type === 'public' && <OtherLayout
          routers={global.routers.PUBLIC_ROUTERS}
          pages={pages}
        />
      }
      <ChatwootWidget />
    </Layout>
  )
}

export default App
